class ProductVariantSwitcher extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.currentProduct = null;
    this.originalProductData = null;
    this.setupEventListeners();
    this.highlightOriginalVariant();
    this.storeOriginalProductData();
  }

  setupEventListeners() {
    const variantElements = this.querySelectorAll('[data-variant-switch]');
    
    variantElements.forEach(variantElement => {
      variantElement.addEventListener('click', (e) => {
        e.preventDefault(); // Prevent navigation
        this.handleVariantSwitch(variantElement);
      });
      
      // Add keyboard support for accessibility
      variantElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleVariantSwitch(variantElement);
        }
      });
    });
  }

  highlightOriginalVariant() {
    const originalVariant = this.querySelector('[data-variant-switch]');
    if (originalVariant) {
      originalVariant.classList.add('selected-variant');
    }
  }

  storeOriginalProductData() {
    // Store the original product data for potential reset
    const productCard = this.closest('product-card');
    if (productCard) {
      this.originalProductData = {
        handle: productCard.getAttribute('handle'),
        title: productCard.querySelector('.product-title')?.textContent?.trim(),
        price: productCard.querySelector('.card__price')?.textContent?.trim(),
        image: productCard.querySelector('.product-card__image--primary')?.src,
        secondaryImage: productCard.querySelector('.product-card__image--secondary')?.src,
        variantId: productCard.querySelector('input[name="id"]')?.value
      };
    }
  }

  async handleVariantSwitch(variantElement) {
    try {
      const cardId = variantElement.dataset.cardId;
      const productHandle = variantElement.dataset.productHandle;
      const productId = variantElement.dataset.productId;
      const productPrice = variantElement.dataset.productPrice;
      const productTitle = variantElement.dataset.productTitle;
      const productImage = variantElement.dataset.productImage;
      const productSubtitle = variantElement.dataset.productSubtitle;
      
      // If clicking the same variant, do nothing
      if (this.currentProduct === productHandle) {
        return;
      }
      
      // Add loading state to the variant switcher
      this.classList.add('variant-switch-loading');
      
      this.currentProduct = productHandle;

      // Get the product card element
      const productCard = this.closest('product-card');
      if (!productCard) throw new Error('Product card not found');

      // Find all necessary elements within the product card
      const form = productCard.querySelector('product-form form');
      const addToCartBtn = productCard.querySelector('button[type="submit"]');
      const mainImageLink = productCard.querySelector('.product-card__media');
      const mainImage = productCard.querySelector('.product-card__image--primary');
      const secondaryImage = productCard.querySelector('.product-card__image--secondary');
      const titleLink = productCard.querySelector('.product-title');
      const priceDisplay = productCard.querySelector('.card__price');
      const subtitleDisplay = productCard.querySelector('.card__subtitle');
      const variantIdInput = productCard.querySelector('input[name="id"]');

      // Show loading state
      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Loading...';
      }

      // Highlight the selected variant
      this.querySelectorAll('[data-variant-switch]').forEach(variant => {
        variant.classList.remove('selected-variant');
      });
      variantElement.classList.add('selected-variant');

      // Fetch the product data
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error(`Failed to fetch product data: ${response.statusText}`);
      
      const productData = await response.json();
      const firstAvailableVariant = productData.variants.find(variant => variant.available);

      // Update the product card handle
      productCard.setAttribute('handle', productHandle);

      // Update the form and variant ID
      if (form && variantIdInput) {
        // Update the variant ID input
        variantIdInput.value = firstAvailableVariant?.id || productData.variants[0]?.id;
        
        // Update form action if needed
        form.action = '/cart/add';
      }

      // Update price with smooth transition
      if (priceDisplay && productPrice) {
        const formattedPrice = `$${parseFloat(productPrice).toFixed(2)}`;
        
        // Add fade out effect
        priceDisplay.style.opacity = '0.5';
        priceDisplay.style.transition = 'opacity 0.2s ease';
        
        // Update price content after fade
        setTimeout(() => {
          // Clear existing price elements and update with new price
          priceDisplay.innerHTML = `
            <span class="card__compare-price body-text" style="color: inherit;">${formattedPrice}</span>
            <span class="card__discount-price body-text !text-red-600 text-[15px]" style="color: #dc2626;" itemprop="price" content="${formattedPrice}">${formattedPrice}</span>
          `;
          
          // Fade back in
          priceDisplay.style.opacity = '1';
        }, 200);
      }

      // Update title
      if (titleLink) {
        titleLink.href = `/products/${productHandle}`;
        titleLink.textContent = productTitle || productData.title;
        titleLink.setAttribute('aria-label', productTitle || productData.title);
      }

      // Update subtitle
      if (subtitleDisplay) {
        const subtitle = productSubtitle || '';
        if (subtitle) {
          subtitleDisplay.textContent = subtitle;
          subtitleDisplay.style.display = '';
        } else {
          subtitleDisplay.textContent = '';
          subtitleDisplay.style.display = 'none';
        }
      }

      // Update main product image with smooth transition
      if (mainImage && productImage) {
        // Add fade out effect
        mainImage.style.opacity = '0.5';
        mainImage.style.transition = 'opacity 0.3s ease';
        
        // Update image after fade
        setTimeout(() => {
          // Set the primary image (first image)
          mainImage.src = productImage;
          mainImage.srcset = productImage;
          mainImage.alt = productTitle || productData.title;
          
          // Fade back in
          mainImage.style.opacity = '1';
        }, 300);
      }

      // Update secondary/hover image if it exists
      if (secondaryImage && productData.media && productData.media.length > 1) {
        // Find the second image (hover image)
        const secondImage = productData.media[1];
        if (secondImage) {
          // Add fade out effect
          secondaryImage.style.opacity = '0.5';
          secondaryImage.style.transition = 'opacity 0.3s ease';
          
          // Update secondary image after fade
          setTimeout(() => {
            // Use the proper Shopify image URL format with transformations
            const imageUrl = secondImage.src || `https://cdn.shopify.com/s/files/1/0139/2302/4944/products/${secondImage.id}.jpg`;
            const transformedUrl = imageUrl.replace(/\.(jpg|jpeg|png|webp)/i, '_800x800.$1');
            
            secondaryImage.src = transformedUrl;
            secondaryImage.srcset = transformedUrl;
            secondaryImage.alt = productTitle || productData.title;
            
            // Fade back in
            secondaryImage.style.opacity = '1';
          }, 300);
        }
      }

      // Update main image link
      if (mainImageLink) {
        mainImageLink.href = `/products/${productHandle}`;
      }

      // Update button state
      if (addToCartBtn) {
        const isAvailable = productData.available;
        addToCartBtn.disabled = !isAvailable;
        addToCartBtn.classList.toggle('sold-out', !isAvailable);
        
        // Update button text based on availability (without price)
        addToCartBtn.textContent = isAvailable ? 'Add to Bag' : 'Sold Out';
      }

      // Remove loading state
      this.classList.remove('variant-switch-loading');

    } catch (error) {
      console.error('Error switching variant:', error);
      // Reset loading state
      this.classList.remove('variant-switch-loading');
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Add to Bag';
      }
    }
  }

  resetToOriginal() {
    if (this.originalProductData) {
      const productCard = this.closest('product-card');
      if (productCard) {
        // Reset product card handle
        productCard.setAttribute('handle', this.originalProductData.handle);
        
        // Reset title
        const titleLink = productCard.querySelector('.product-title');
        if (titleLink) {
          titleLink.href = `/products/${this.originalProductData.handle}`;
          titleLink.textContent = this.originalProductData.title;
        }
        
        // Reset price
        const priceDisplay = productCard.querySelector('.card__price');
        if (priceDisplay) {
          priceDisplay.innerHTML = this.originalProductData.price;
        }
        
        // Reset image
        const mainImage = productCard.querySelector('.product-card__image--primary');
        if (mainImage && this.originalProductData.image) {
          mainImage.src = this.originalProductData.image;
          mainImage.alt = this.originalProductData.title;
        }
        
        // Reset secondary image if it exists
        const secondaryImage = productCard.querySelector('.product-card__image--secondary');
        if (secondaryImage && this.originalProductData.secondaryImage) {
          secondaryImage.src = this.originalProductData.secondaryImage;
          secondaryImage.alt = this.originalProductData.title;
        }
        
        // Reset variant ID
        const variantIdInput = productCard.querySelector('input[name="id"]');
        if (variantIdInput) {
          variantIdInput.value = this.originalProductData.variantId;
        }
        
        // Reset button
        const addToCartBtn = productCard.querySelector('button[type="submit"]');
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Add to Bag';
        }
      }
    }
    
    // Reset variant selection
    this.querySelectorAll('[data-variant-switch]').forEach(variant => {
      variant.classList.remove('selected-variant');
    });
    
    // Highlight first variant
    const firstVariant = this.querySelector('[data-variant-switch]');
    if (firstVariant) {
      firstVariant.classList.add('selected-variant');
    }
    
    this.currentProduct = null;
  }

  // Method to refresh event listeners when content is dynamically updated
  refreshEventListeners() {
    this.setupEventListeners();
  }

  // Method to add a new variant dynamically
  addVariant(variantData) {
    const variantElement = document.createElement('button');
    variantElement.type = 'button';
    variantElement.className = 'associated-product variant-switch-button';
    variantElement.setAttribute('data-variant-switch', '');
    variantElement.setAttribute('data-product-handle', variantData.handle);
    variantElement.setAttribute('data-product-id', variantData.id);
    variantElement.setAttribute('data-product-title', variantData.title);
    variantElement.setAttribute('data-product-price', variantData.price);
    variantElement.setAttribute('data-product-image', variantData.image);
    variantElement.setAttribute('data-product-subtitle', variantData.subtitle || '');
    variantElement.setAttribute('data-card-id', variantData.cardId);
    variantElement.setAttribute('aria-label', `Switch to ${variantData.title}`);
    
    variantElement.innerHTML = `
      <div>
        <img
          src="${variantData.image}"
          alt="${variantData.title}"
          loading="lazy"
          width="100%"
          height="100%">
      </div>
    `;
    
    // Add event listeners to the new element
    variantElement.addEventListener('click', (e) => {
      e.preventDefault();
      this.handleVariantSwitch(variantElement);
    });
    
    variantElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleVariantSwitch(variantElement);
      }
    });
    
    return variantElement;
  }
}

// Register the custom element
customElements.define('product-variant-switcher', ProductVariantSwitcher); 